import React, { useState, useEffect } from "react";
import { Box, Tooltip, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import Filters, { FilterProps } from "./Filters";
import * as calFns from "../../components/Calendar/functions/calendarFunctions";
import useMobileScreen from "../../hooks/useMobileScreen";
import { RootState } from "../../store";
import { SmsReportData } from "../../interfaces/message";
import ListHeaderShowHideDialog from "../../components/ListHeaderShowHideDialog/ListHeaderShowHideDialog";
import {
  getDefaultKeyValuesColumns,
  smsDefaultCols,
  smsHiddenCols
} from "../../components/ListHeaderShowHideDialog/helpers";
import List, { EmptyView, ListActions, Menu, MenuItem } from "../../components/OList";
import { downloadExcel } from "../../helpers/files";
import { formatDataForExcel, formatHeaderForExcel } from "../accounts/Reports/helper";
import useCurrentResourceCentre from "../../hooks/useCurrentResourceCentre";
import { getSMSReport } from "../../api/reports";
import Can from "../Policy/Can";
import PageControl from "../../components/PageControl";
import useIsReactNativeWebView from "../../hooks/useIsReactNativeWebView";
import { t } from "../../components/translate";
import { notificationAdd } from "../../actions/notification";

// css
import "./Reports.scss";
import { COLUMN_STORAGE_KEYS, useListColumnVisibility } from "../../hooks/useListColumnVisibility";

const pageSize = 50;
const SMS_STATUSES: Record<number, string> = {
  1000: "A required field is missing",
  1001: "Invalid IP Address",
  1002: "Invalid Token",
  1003: "Account Inactiv$",
  1004: "Account Inactive",
  1005: "Account has been expired",
  1006: "Account has been expired",
  1007: "Invalid Receiver",
  1008: "Invalid Sender",
  1010: "Text cannot be empty",
  1011: "No valid receiver",
  1012: "No Credits Available",
  1013: "Insufficient Credits",
  2000: "Network Error"
};

const docColumns = () => [
  t("msgReport.Date"),
  t("msgReport.PhNo."),
  "Status",
  t("msgReport.msgType"),
  t("msgReport.smsContent")
];

const docRowProcessor = (row: SmsReportData) => [
  calFns.convertADtoBS(new Date(row.created_at)).formatted,
  row.phone,
  SMS_STATUSES[row.smsStatus] || row.smsStatus,
  "SMS",
  row.sms
];

const SMSReport = (): React.ReactNode => {
  const dispatch = useDispatch();
  const rc = useCurrentResourceCentre();
  const isMobileScreen = useMobileScreen();
  const { isRnWebView } = useIsReactNativeWebView();
  const { columns: listColumns, setColumns: setListColumns } = useListColumnVisibility(
    COLUMN_STORAGE_KEYS.MESSAGE_REPORT_COLUMNS,
    getDefaultKeyValuesColumns(smsDefaultCols, smsHiddenCols)
  );

  // selector
  const resourceCentreId = useSelector((state: RootState) => state.userContext.resourceCentreId);

  // state
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [reportData, setReportData] = useState<SmsReportData[]>([]);
  const [isRequestLoading, setIsRequestLoading] = React.useState(false);
  const [filters, setFilters] = useState<FilterProps>({
    from: calFns.startOfDay(new Date()).toDate(),
    until: new Date(),
    messageStatus: "Fail"
  });

  // effects
  useEffect(() => {
    (async () => {
      try {
        setIsRequestLoading(true);
        const data = await getSMSReport({
          ...filters,
          messageStatus: filters.messageStatus || "Fail",
          resourceCentreId,
          page,
          pageSize
        });
        setTotal(data.total);
        setReportData(data.results);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (err: any) {
        dispatch(
          notificationAdd({
            id: new Date().getUTCMilliseconds(),
            variant: "error",
            message: err?.data?.message || "Failed to fetch SMS report",
            autoTimeout: true
          })
        );
      } finally {
        setIsRequestLoading(false);
      }
    })();
  }, [filters, page, resourceCentreId, dispatch]);

  return (
    <Can policyAccessKey="report:smsReport">
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* filter */}
        <Box sx={{ px: 3, py: 2 }}>
          <Filters
            filters={filters}
            onSetFilters={(newfilters) => {
              setFilters(newfilters);
              setPage(0);
            }}
            isRequestLoading={isRequestLoading}
          />
        </Box>

        {/* list */}
        <Box
          className="smsReportList"
          sx={{
            borderTop: 1,
            borderColor: "divider",
            width: "100%",
            overflow: "auto"
          }}
        >
          <Box sx={{ minWidth: isMobileScreen || isRnWebView ? "900px" : "auto", mb: "40px" }}>
            <List
              automation="smsReportList"
              data={reportData}
              rowHeight={40}
              defaultSortColumn="clientName"
              defaultSortOrder={-1}
              activeRow={0}
              adjustHeightToContents
              columns={[
                {
                  key: "created_at",
                  label: `${t("msgReport.Date")}`,
                  formatter: (row: SmsReportData) => (
                    <Typography>
                      {calFns.convertADtoBS(new Date(row.created_at)).formatted}
                    </Typography>
                  )
                },
                {
                  key: "phone",
                  label: `${t("msgReport.PhNo.")}`,
                  formatter: (row: SmsReportData) => <Typography>{row.phone}</Typography>
                },
                {
                  key: "status",
                  label:
                    filters.messageStatus === "Fail"
                      ? `${t("msgReport.reasonOfFailure")}`
                      : "SMS Status",
                  formatter: (row: SmsReportData) => (
                    <Typography>{SMS_STATUSES[row.smsStatus] || row.smsStatus}</Typography>
                  )
                },
                {
                  key: "type",
                  label: `${t("msgReport.msgType")}`,
                  formatter: () => <Typography>SMS</Typography>
                },
                {
                  key: "sms",
                  label: `${t("msgReport.smsContent")}`,
                  formatter: (row: SmsReportData) => (
                    <Tooltip title={row.sms}>
                      <Typography>{row.sms.substring(0, 20)}...</Typography>
                    </Tooltip>
                  )
                }
              ].filter((col) => listColumns[col.key])}
            >
              <EmptyView>
                <Box textAlign="center" padding="50px">
                  {t("msgReport.noItemsToShow")}
                </Box>
              </EmptyView>
              <ListActions>
                {/* eslint-disable-next-line react/no-unused-prop-types */}
                {({ getProcessedData }: { getProcessedData: () => SmsReportData[] }) => (
                  <Menu>
                    <MenuItem
                      onClick={async () => {
                        try {
                          await downloadExcel(
                            "SMS Report",
                            formatHeaderForExcel(docColumns()),
                            formatDataForExcel(getProcessedData(), docRowProcessor),
                            rc,
                            {
                              from: filters.from,
                              until: filters.until
                            }
                          );
                        } catch (err) {
                          dispatch(
                            notificationAdd({
                              id: new Date().getUTCMilliseconds(),
                              variant: "error",
                              message: "Failed to download Excel.",
                              autoTimeout: true
                            })
                          );
                        }
                      }}
                    >
                      Excel
                    </MenuItem>
                    <MenuItem onClick={() => setOpen(true)}>Show/Hide Columns</MenuItem>
                  </Menu>
                )}
              </ListActions>
            </List>

            <Box className="labNavigatePage">
              <PageControl
                page={page}
                pageSize={pageSize}
                onSetPage={(v: number) => {
                  setPage(v);
                }}
                maximumDataCount={total}
              />
            </Box>
          </Box>
        </Box>

        {/* panels and dialogs */}
        <>
          <ListHeaderShowHideDialog
            onChange={(updatedColumns) => setListColumns(updatedColumns)}
            open={open}
            onClose={() => setOpen(false)}
            columns={listColumns}
          />
        </>
      </Box>
    </Can>
  );
};

export default SMSReport;
